/**
 * Date formatting utilities for consistent display across the application.
 * All dates are displayed in unambiguous "Fri 17 Nov 2023" format.
 */

// Short weekday and month name arrays for manual formatting (timezone-safe)
const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Kept for backwards-compatibility – returns a stable config object.
 * The actual display format is now always "Fri 17 Nov 2023".
 */
const getDateFormat = (userPreference = null, agentCountry = null, agencyCountry = null) => {
  return { format: 'DD MMM YYYY', locale: 'en-GB' };
};

/**
 * Get default format – kept for compatibility.
 */
const getDefaultFormat = () => getDateFormat();

/**
 * Extract date parts from a date string (handles timezone safely)
 */
const parseDateParts = (dateString) => {
  // Parse as UTC to avoid timezone shifts
  const date = new Date(dateString);
  // If the input is a date-only string like '2026-03-19', parse manually to avoid TZ issues
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-');
    return { year, month, day };
  }
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return { year, month, day };
};

/**
 * Format date for display as "Fri 17 Nov 2023".
 * This unambiguous format is used consistently across the entire application.
 * The options parameter is accepted for backwards compatibility but is not used.
 */
const formatDate = (dateString, options = {}) => {
  if (!dateString) return '-';

  try {
    const { year, month, day } = parseDateParts(dateString);
    // Build a local Date from parts to get the correct weekday (timezone-safe)
    const dateObj = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    const weekday = WEEKDAY_NAMES[dateObj.getDay()];
    const monthName = MONTH_NAMES[parseInt(month, 10) - 1];
    return `${weekday} ${parseInt(day, 10)} ${monthName} ${year}`;
  } catch (error) {
    console.error('Error formatting date:', error, dateString);
    return dateString;
  }
};

/**
 * Format date with long format – now identical to formatDate ("Fri 17 Nov 2023").
 */
const formatDateLong = (dateString, options = {}) => {
  return formatDate(dateString, options);
};

/**
 * Format date for HTML input (always YYYY-MM-DD regardless of display preference)
 */
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Always YYYY-MM-DD for inputs
  } catch (error) {
    console.error('Error formatting date for input:', error, dateString);
    return '';
  }
};

/**
 * Format date with short format – now identical to formatDate ("Fri 17 Nov 2023").
 */
const formatDateShort = (dateString, options = {}) => {
  return formatDate(dateString, options);
};

/**
 * Get current date format string (for display purposes).
 * Always returns the unified unambiguous format label.
 */
const getCurrentDateFormat = (userPreference = null, agentCountry = null, agencyCountry = null) => {
  return 'DD MMM YYYY';
};

/**
 * Get example date string for UI hints – uses the unified format.
 */
const getExampleDate = (userPreference = null, agentCountry = null, agencyCountry = null) => {
  return formatDate('2026-03-20');
};

/**
 * Validate date string
 */
const isValidDate = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Parse display date back to ISO format.
 * The display format is "Fri 17 Nov 2023" – this function handles ISO inputs too
 * since date inputs always submit in YYYY-MM-DD.
 */
const parseDateFromDisplay = (displayDate, userPreference = null, agentCountry = null, agencyCountry = null) => {
  if (!displayDate) return '';

  try {
    // If already in ISO format, return as-is
    if (/^\d{4}-\d{2}-\d{2}/.test(displayDate)) {
      return displayDate.substring(0, 10);
    }

    // Try to parse "Fri 17 Nov 2023" format
    const months = { Jan:1, Feb:2, Mar:3, Apr:4, May:5, Jun:6, Jul:7, Aug:8, Sep:9, Oct:10, Nov:11, Dec:12 };
    const parts = displayDate.trim().split(/\s+/);
    if (parts.length >= 3) {
      // parts[0] = "Fri", parts[1] = "17", parts[2] = "Nov", parts[3] = "2023"
      const dayPart = parts[1];
      const monthPart = parts[2];
      const yearPart = parts[3] || parts[2]; // fallback
      const monthNum = months[monthPart];
      if (monthNum && dayPart && yearPart) {
        return `${yearPart.padStart(4,'0')}-${String(monthNum).padStart(2,'0')}-${dayPart.padStart(2,'0')}`;
      }
    }
    return '';
  } catch (error) {
    console.error('Error parsing display date:', error, displayDate);
    return '';
  }
};

// CommonJS exports for Node.js compatibility
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    formatDate,
    formatDateLong,
    formatDateForInput,
    formatDateShort,
    getCurrentDateFormat,
    getExampleDate,
    isValidDate,
    parseDateFromDisplay,
    getDateFormat
  };
}

// Named exports for ES module imports
export {
  formatDate,
  formatDateLong,
  formatDateForInput,
  formatDateShort,
  getCurrentDateFormat,
  getExampleDate,
  isValidDate,
  parseDateFromDisplay,
  getDateFormat
};

export default {
  formatDate,
  formatDateLong,
  formatDateForInput,
  formatDateShort,
  getCurrentDateFormat,
  getExampleDate,
  isValidDate,
  parseDateFromDisplay,
  getDateFormat
};
